// src/Components/StoreDetail.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch store details
  useEffect(() => {
    const fetchStore = async () => {
      try {
        const storeRef = doc(db, "Stores", id);
        const docSnap = await getDoc(storeRef);
        if (docSnap.exists()) {
          setStore(docSnap.data());
        } else {
          setStore(null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [id]);

  // Fetch products for this store
  useEffect(() => {
    if (!id) return;

    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const q = query(productsRef, where("storeId", "==", id));
        const querySnapshot = await getDocs(q);

        const productsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setProducts(productsData);
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };

    fetchProducts();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!store) return <div className="min-h-screen flex items-center justify-center text-red-500">Store not found.</div>;

  // Helper to get product image safely
  const getProductImage = (product) => {
    if (product.imageUrl) return product.imageUrl;
    if (product.image) return product.image;
    if (product.images && product.images.length > 0) return product.images[0];
    return "https://via.placeholder.com/200";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-12 flex flex-col items-center">
      <button
        onClick={() => navigate(-1)}
        className="self-start mb-4 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
      >
        Back
      </button>

      {/* Store Info */}
      <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-6 mb-8">
        <img
          src={store.imageUrl || store.image || "https://via.placeholder.com/400"}
          alt={store.name}
          className="w-full h-64 md:h-96 object-cover rounded-lg mb-6"
        />
        <h1 className="text-3xl font-bold mb-2">{store.name}</h1>
        <p className="text-gray-600 mb-4">{store.description}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h3 className="font-semibold text-gray-700">Address</h3>
            <p className="text-gray-600">{store.address}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700">Phone</h3>
            <p className="text-gray-600">{store.phone}</p>
          </div>
          {store.email && (
            <div>
              <h3 className="font-semibold text-gray-700">Email</h3>
              <p className="text-gray-600">{store.email}</p>
            </div>
          )}
          {store.openTime && store.closeTime && (
            <div>
              <h3 className="font-semibold text-gray-700">Working Hours</h3>
              <p className="text-gray-600">{store.openTime} - {store.closeTime}</p>
            </div>
          )}
        </div>
      </div>

      {/* Products Section */}
      {products.length > 0 && (
        <div className="w-full max-w-6xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Products from {store.name}</h2>
            <button
              onClick={() => navigate(`/shop-products/${id}`)}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              See All
            </button>
          </div>

          <div className="flex space-x-4 overflow-x-auto py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {products.slice(0, 5).map((product) => {
              const productImage = getProductImage(product);
              const originalPrice = Number(product.price) || 0;
              const discountedPrice = (originalPrice * 0.7).toFixed(2); // 30% off

              return (
                <div
                  key={product.id}
                  className="flex-shrink-0 w-48 bg-white rounded-lg shadow hover:shadow-lg transition p-1 cursor-pointer relative"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <img
                    src={productImage}
                    alt={product.name}
                    className="w-full h-40 object-cover rounded mb-2 bg-[#e5e5e5]"
                  />
                  <h3 className="text-lg font-semibold">{product.name}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{product.description}</p>
                  <div className="mt-1 flex items-center gap-1">
                    
                    <span className="text-indigo-600 font-bold text-sm">₹ {discountedPrice}</span>
                    <span className="text-gray-400 line-through text-sm">₹ {originalPrice}</span>
                  </div>
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                    30% OFF
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreDetail;
